/**
 * Display formatting + the FE-owned derivations that are pure functions of data the
 * screen already has (contract §3). Screens and their tests both import from here so
 * there is exactly one implementation of each rule.
 */

import type { EnquiryStatus, EnquiryType, Item, Money, UomConversion } from './types'

/* ============ money ============ */

/**
 * `{ amount: 205, currency: 'AED' }` → "AED 205.00". Formatting is display-only.
 *
 * NEVER THROWS. `Intl.NumberFormat` raises `RangeError: currency is not a well-formed currency
 * code` for anything that is not three letters — and `Currency` is a bare `string` alias, so an
 * empty or malformed code reaches here from any of a dozen `as Currency` casts across the app
 * (a supplier's parsed workbook with no currency, a company with none set, a wire field that came
 * back null). One of those took down a whole route with "Unexpected Application Error".
 *
 * A formatter is the last place that should be able to do that. Money still renders — the number,
 * with whatever code we were given beside it — so the screen stays usable and the bad value is
 * visible rather than fatal. The console warning names it once so the SOURCE stays findable
 * instead of being papered over here.
 */
export function formatMoney(money: Money | null | undefined, maximumFractionDigits = 2): string {
  if (!money) return '—'
  try {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: money.currency,
      maximumFractionDigits,
    }).format(money.amount)
  } catch {
    warnBadCurrency(money.currency)
    const amount = new Intl.NumberFormat('en-AE', { maximumFractionDigits }).format(money.amount)
    // A code we cannot format is still worth showing: an unlabelled number in an app that quotes
    // in several currencies is its own hazard.
    return money.currency ? `${money.currency} ${amount}` : amount
  }
}

/** Warn once per bad code — a broken currency in a table would otherwise flood the console. */
const warnedCurrencies = new Set<string>()
function warnBadCurrency(currency: string): void {
  if (warnedCurrencies.has(currency)) return
  warnedCurrencies.add(currency)
  console.warn(
    `formatMoney: ${JSON.stringify(currency)} is not a currency code Intl accepts — showing the ` +
      `amount unformatted. Find where this Money was built; the code is wrong at its source.`,
  )
}

export function formatNumber(n: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('en-AE', { maximumFractionDigits }).format(n)
}

export const money = (amount: number, currency: Money['currency'] = 'AED'): Money => ({
  amount,
  currency,
})

/* ============ dates / age ============ */

/**
 * Whole days between an ISO-8601 timestamp and now. Age is ALWAYS derived here —
 * the API never stores a day count (contract §1).
 */
export function daysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 0
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000))
}

/** "2d" / "today" — the requirement age chip. */
export function formatAge(iso: string, now?: Date): string {
  const d = daysSince(iso, now)
  return d === 0 ? 'today' : `${d}d`
}

/**
 * A complete age phrase: "today" / "1 day ago" / "5 days ago".
 *
 * Use this wherever the age is read as a sentence. Appending " ago" to `formatAge` produces
 * "today ago" on the same day, which is why the two are separate functions.
 */
export function formatAgo(iso: string, now?: Date): string {
  const d = daysSince(iso, now)
  if (d === 0) return 'today'
  return d === 1 ? '1 day ago' : `${d} days ago`
}

/**
 * The "Awaiting N days" badge for the requirement on screen. Task 7: `age_days` is now
 * server-computed (`quoflow.enquiry.get_enquiry`), not a client-side `daysSince` approximation —
 * this is a passthrough, kept as a named function so call sites still read "awaiting N days"
 * rather than a bare field access, and so a future backend reshaping of the field has one place
 * to absorb it.
 */
export function awaitingDays(ageDays: number): number {
  return ageDays
}

export function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-AE', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
}

export function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-AE', { hour: '2-digit', minute: '2-digit' }).format(d)
}

/* ============ status ============ */

export type BadgeTone = 'ok' | 'warn' | 'info' | 'danger' | 'violet' | 'muted' | 'teal'

/** Status → badge tone, matching the prototype's colour choices, remapped onto the server's nine
 * statuses (Task 7). */
export function statusTone(status: EnquiryStatus): BadgeTone {
  switch (status) {
    case 'Draft':
      return 'muted'
    case 'Open':
      return 'info'
    case 'RFQ Sent':
      return 'teal'
    case 'Quotes Received':
      return 'warn'
    case 'Comparison Done':
      return 'violet'
    case 'Quotation Sent':
      return 'info'
    case 'Won':
      return 'ok'
    case 'Rejected':
      return 'danger'
    case 'Archived':
      return 'muted'
  }
}

/**
 * A `Quotation Sent` enquiry older than 30 days shows as `Archived` (DATA-MODEL: "Quoted older
 * than 30 days auto-archives"). Display-level only — the stored status is untouched.
 *
 * Task 7: `ageDays` is the server-computed `age_days`, not a client `daysSince(status_changed_at)`
 * read — so there is no `now` to inject any more; the day count is already fixed by the server's
 * own read.
 */
export function effectiveStatus(status: EnquiryStatus, ageDays: number): EnquiryStatus {
  if (status === 'Quotation Sent' && ageDays > 30) return 'Archived'
  return status
}

/** The enquiry-type chip's tone — shared by the board card and the flow context bar. */
export function enquiryTypeTone(type: EnquiryType): BadgeTone {
  switch (type) {
    case 'Provision':
      return 'violet'
    case 'Commercial':
      return 'teal'
    case 'Bonded Stores':
      return 'warn'
    case 'Technical':
      return 'info'
  }
}

export function confidenceTone(confidence: number): BadgeTone {
  return confidence >= 0.85 ? 'ok' : confidence >= 0.6 ? 'warn' : 'danger'
}

/* ============ UOM (FE-owned: contract §3, §6) ============ */

/**
 * Factor of `uom` relative to the item's base UOM, or `null` when the UOM is not in the
 * item master. The server is authoritative on write (422 UOM_NOT_IN_MASTER) — this is
 * the instant mirror so the purchaser sees it while typing.
 */
export function uomFactor(item: Pick<Item, 'base_uom' | 'uom_conversions'>, uom: string): number | null {
  // Base first, so this holds whether or not the backend lists the base UOM in
  // `uom_conversions` (contract §4's example omits it; §6's `available` includes it).
  if (uom === item.base_uom) return 1
  const conv = item.uom_conversions.find((c: UomConversion) => c.uom === uom)
  return conv ? conv.factor : null
}

/** Qty expressed in the item's base UOM, or `null` if the UOM is unknown to the master. */
export function qtyInBaseUom(
  item: Pick<Item, 'base_uom' | 'uom_conversions'>,
  qty: number,
  uom: string,
): number | null {
  const factor = uomFactor(item, uom)
  return factor === null ? null : qty * factor
}

/**
 * The soft "qty × factor > on-hand" warning. A warning, never a block — the server may
 * echo it in a non-blocking `warnings` array (contract §6).
 */
export function exceedsOnHand(
  item: Pick<Item, 'base_uom' | 'uom_conversions' | 'on_hand'>,
  qty: number,
  uom: string,
): boolean {
  // No stock source, no warning. `on_hand` is optional (see `Item.on_hand`) because Quoflow's
  // `Item` carries no stock quantity — treating that absence as 0 made this true for every line
  // with a qty, which is a false alarm, not a conservative default.
  if (item.on_hand === undefined) return false
  const baseQty = qtyInBaseUom(item, qty, uom)
  return baseQty !== null && baseQty > item.on_hand
}

/* ============ allocation-derived (FE-owned: contract §8) ============ */

// The blended average, coverage and the allocation strategies live in `lib/compare.ts`,
// which owns the comparison workbench's math. They were stubbed here during scaffolding;
// keeping a second implementation around is how the two drift apart.

export function formatPct(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`
}

/* ============ misc ============ */

export function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || '?'
  ).toUpperCase()
}
